import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { useSignals } from "@/hooks/useNews";
import { type NormalizedSignal, type SignalStatus, type SignalConfidence } from "@/lib/types";
import {
  availableHazardLayers,
  filterMapSignals,
  isLocationOnlySignal,
  signalHazardCategory,
  signalMapGeometry,
} from "@/lib/mapsModel";
import {
  EMPTY_COUNTRY_BOUNDARIES,
  loadWhoCountryBoundaries,
  type CountryBoundaryIndex,
  whoCountryBoundary,
} from "@/lib/whoCountryBoundaries";
import { Helmet } from "react-helmet-async";
import { Loader2, RefreshCw, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, GeoJSON, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

declare const __TSM_CARTO_BASEMAP_KEY__: string;

// ---------------------------------------------------------------------------
// Safari fix — invalidate map size after tiles load
// ---------------------------------------------------------------------------

function MapResizeOnMount() {
  const map = useMap();
  useEffect(() => {
    // Safari needs invalidateSize after the browser has painted tiles;
    // 300ms covers slow Safari compositing + retina tile fetches.
    const t1 = setTimeout(() => map.invalidateSize(), 300);
    // Second pass handles cases where CSS finishes applying after first pass.
    const t2 = setTimeout(() => map.invalidateSize(), 800);
    // Also fire on window resize so the map stays correct during layout shifts.
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", onResize);
    };
  }, [map]);
  return null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_CENTER: [number, number] = [20, 10];
const DEFAULT_ZOOM = 2;
const CARTO_DARK_MATTER_URL = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

export function cartoBasemapTileUrl(key = __TSM_CARTO_BASEMAP_KEY__): string {
  const normalized = key.trim();
  return normalized ? `${CARTO_DARK_MATTER_URL}?key=${encodeURIComponent(normalized)}` : CARTO_DARK_MATTER_URL;
}

const CATEGORY_COLOR: Record<string, string> = {
  seismic: "#ff9100",
  weather: "#00e5cc",
  flood: "#00a8ff",
  fire: "#ff4d2e",
  landslide: "#b48a5a",
  volcano: "#e25cff",
  coastal: "#24c7ff",
  drought: "#d4b24c",
  health: "#63d471",
  geopolitical: "#4da6ff",
  "infrastructure-supply-chain": "#c4c4c4",
};
const CATEGORY_COLOR_FALLBACK = "#aaa";

// Status opacity — answers "how fresh is this signal?"
const STATUS_OPACITY: Record<SignalStatus, number> = {
  live:     0.85,
  cached:   0.45,
  stale:    0.20,
  fallback: 0.20,
  stub:     0.10,
  error:    0.15,
};

const CONFIDENCE_RADIUS: Record<SignalConfidence, number> = {
  high: 10,
  medium: 7,
  low: 5,
};

// ---------------------------------------------------------------------------
// Hazard layer controls
// ---------------------------------------------------------------------------

function HazardToggles({
  layers,
  enabled,
  onToggle,
}: {
  layers: string[];
  enabled: Set<string>;
  onToggle: (layer: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {layers.map((layer) => {
        const on = enabled.has(layer);
        return (
          <button
            key={layer}
            onClick={() => onToggle(layer)}
            style={{
              background: on ? "#1a1a1a" : "none",
              border: `1px solid ${on ? "#ff9100" : "#333"}`,
              color: on ? "#ff9100" : "#555",
              padding: "3px 10px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.07em",
              cursor: "pointer",
            }}
          >
            {layer}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Age label helper
// ---------------------------------------------------------------------------

function ageLabel(iso?: string): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// Signal popup
// ---------------------------------------------------------------------------

function SignalPopup({ signal, usesCountryBoundary = false }: { signal: NormalizedSignal; usesCountryBoundary?: boolean }) {
  const category = signalHazardCategory(signal) ?? signal.category;
  const categoryColor = CATEGORY_COLOR[category] ?? CATEGORY_COLOR_FALLBACK;
  return (
    <div style={{ minWidth: 200, maxWidth: 280, fontFamily: "monospace" }}>
      <div style={{ marginBottom: 6, display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            border: `1px solid ${categoryColor}`,
            color: categoryColor,
            padding: "1px 5px",
          }}
        >
          {signal.source}
        </span>
        <span style={{ fontSize: 9, color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {category} · {signal.status}
        </span>
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, margin: "0 0 5px", lineHeight: 1.3, color: "#f0f0f0" }}>
        {signal.title}
      </p>
      {signal.summary && (
        <p style={{ fontSize: 11, color: "#aaa", margin: "0 0 5px", lineHeight: 1.4 }}>
          {signal.summary.slice(0, 120)}{signal.summary.length > 120 ? "…" : ""}
        </p>
      )}
      {signal.marketImpact && (
        <p style={{ fontSize: 10, color: "#888", margin: "0 0 5px" }}>
          <span style={{ color: "#ff9100", fontWeight: 700 }}>Impact: </span>
          {signal.marketImpact.slice(0, 80)}{signal.marketImpact.length > 80 ? "…" : ""}
        </p>
      )}
      {usesCountryBoundary && (
        <p style={{ fontSize: 10, color: "#63d471", margin: "0 0 5px" }}>
          Country scope · WHO title scope · Natural Earth boundary
        </p>
      )}
      <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>
        {signal.location && <span>{signal.location} · </span>}
        {ageLabel(signal.observedAt)}
        {signal.url && (
          <a
            href={signal.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#ff9100", marginLeft: 8 }}
          >
            Source ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Maps: React.FC = () => {
  const { data, isLoading, isError, refetch, isFetching } = useSignals();
  const [disabledLayers, setDisabledLayers] = useState(new Set<string>());
  const [showStale, setShowStale] = useState(false);
  const [minConfidence, setMinConfidence] = useState<SignalConfidence>("low");
  const [countryBoundaries, setCountryBoundaries] = useState<CountryBoundaryIndex>(EMPTY_COUNTRY_BOUNDARIES);

  // react-leaflet needs the CSS to be applied before mount
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => { setMapReady(true); }, []);
  useEffect(() => {
    let active = true;
    void loadWhoCountryBoundaries().then((boundaries) => {
      if (active) setCountryBoundaries(boundaries);
    });
    return () => { active = false; };
  }, []);

  const allSignals = data?.signals ?? [];
  const layers = availableHazardLayers(allSignals);
  const enabledLayers = new Set(layers.filter((layer) => !disabledLayers.has(layer)));
  const filteredSignals = filterMapSignals(allSignals, enabledLayers, showStale, minConfidence);
  const geoSignals = filteredSignals.filter((signal) => signalMapGeometry(signal, countryBoundaries));
  const locationOnlySignals = filteredSignals.filter((signal) => (
    !signalMapGeometry(signal, countryBoundaries) && isLocationOnlySignal(signal)
  ));

  function toggleLayer(layer: string) {
    setDisabledLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layer)) next.delete(layer); else next.add(layer);
      return next;
    });
  }

  return (
    <>
      <Helmet>
        <title>Maps — Geo Signal Map | Tylee Smart Markets</title>
        <meta name="description" content="Live geospatial map of provider-supplied event geometry and explicit WHO country outbreak scopes." />
        <link rel="canonical" href="https://tyl.ee/maps" />
      </Helmet>

      <div className="tsm-home-shell theme-amber" style={{ minHeight: "100vh" }}>
        <a href="#maps-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Maps" />

        <main id="maps-main" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 18px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <p style={{ color: "#ff9100", fontWeight: 800, letterSpacing: "0.08em", margin: 0, fontSize: 11, textTransform: "uppercase" }}>
                TSM MAPS
              </p>
              <h1 style={{ color: "#fff", fontSize: 22, fontWeight: 800, margin: "4px 0 0", lineHeight: 1.1 }}>
                Geo Signal Map
              </h1>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {data && (
                <span style={{ color: "#555", fontSize: 11 }}>
                  {geoSignals.length} mapped · {data.liveCount}/{data.totalCount} live
                </span>
              )}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                style={{
                  background: "none",
                  border: "1px solid #333",
                  color: "#888",
                  padding: "5px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                }}
              >
                <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Controls row */}
          <div style={{ display: "flex", gap: 20, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <p style={{ color: "#444", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 5px" }}>Hazard layers</p>
              <HazardToggles layers={layers} enabled={enabledLayers} onToggle={toggleLayer} />
            </div>
            <div>
              <p style={{ color: "#444", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 5px" }}>Min Confidence</p>
              <div style={{ display: "flex", gap: 4 }}>
                {(["high", "medium", "low"] as SignalConfidence[]).map((c) => (
                  <button
                    key={c}
                    onClick={() => setMinConfidence(c)}
                    style={{
                      background: minConfidence === c ? "#1a1a1a" : "none",
                      border: `1px solid ${minConfidence === c ? "#ff9100" : "#333"}`,
                      color: minConfidence === c ? "#ff9100" : "#555",
                      padding: "3px 10px",
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: "0.07em",
                      cursor: "pointer",
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={showStale}
                  onChange={(e) => setShowStale(e.target.checked)}
                  style={{ accentColor: "#ff9100" }}
                />
                <span style={{ color: "#555", fontSize: 11 }}>Show stale/error</span>
              </label>
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            {layers.map((layer) => (
              <span key={layer} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: CATEGORY_COLOR[layer] ?? CATEGORY_COLOR_FALLBACK }} />
                <span style={{ color: "#888", fontSize: 10, fontWeight: 700 }}>{layer}</span>
              </span>
            ))}
            <span style={{ color: "#444", fontSize: 10, marginLeft: 4 }}>· opacity = freshness · marker size = confidence</span>
          </div>

          {/* Map */}
          {isLoading ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#555", padding: "60px 0" }}>
              <Loader2 size={16} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Fetching geo signals…</span>
            </div>
          ) : isError ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#ff4444", padding: "60px 0" }}>
              <XCircle size={16} />
              <span style={{ fontSize: 13 }}>Failed to load signals. Check server logs.</span>
            </div>
          ) : mapReady ? (
            <div style={{ border: "1px solid #1a1a1a" }}>
              <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: 520, width: "100%" }}
                scrollWheelZoom
              >
                <MapResizeOnMount />
                <TileLayer
                  url={cartoBasemapTileUrl()}
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                {geoSignals.map((signal) => {
                  const geometry = signalMapGeometry(signal, countryBoundaries)!;
                  const usesCountryBoundary = whoCountryBoundary(signal, countryBoundaries) === geometry;
                  const category = signalHazardCategory(signal) ?? signal.category;
                  const color = CATEGORY_COLOR[category] ?? CATEGORY_COLOR_FALLBACK;
                  const opacity = STATUS_OPACITY[signal.status] ?? 0.4;
                  const pathOptions = { color, fillColor: color, fillOpacity: opacity, opacity: 1, weight: 1.5 };
                  if (geometry.type === "Point") {
                    return (
                      <CircleMarker key={signal.id} center={[geometry.coordinates[1], geometry.coordinates[0]]} radius={CONFIDENCE_RADIUS[signal.confidence]} pathOptions={pathOptions}>
                        <Popup><SignalPopup signal={signal} usesCountryBoundary={usesCountryBoundary} /></Popup>
                      </CircleMarker>
                    );
                  }
                  return (
                    <GeoJSON key={signal.id} data={geometry as any} pathOptions={pathOptions}>
                      <Popup><SignalPopup signal={signal} usesCountryBoundary={usesCountryBoundary} /></Popup>
                    </GeoJSON>
                  );
                })}
              </MapContainer>
            </div>
          ) : null}

          {/* No-geo note */}
          {!isLoading && !isError && geoSignals.length === 0 && (
            <div style={{ marginTop: 12, color: "#555", fontSize: 12 }}>
              No mapped geometry matches current filters. Maps require provider event geometry or an explicit WHO country title scope.
            </div>
          )}

          {locationOnlySignals.length > 0 && (
            <section aria-labelledby="location-scopes-heading" style={{ marginTop: 18, borderTop: "1px solid #1a1a1a", paddingTop: 14 }}>
              <h2 id="location-scopes-heading" style={{ color: "#ddd", fontSize: 13, margin: "0 0 8px" }}>Provider location scopes without point geometry</h2>
              {locationOnlySignals.map((signal) => (
                <div key={signal.id} style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>
                  <span style={{ color: CATEGORY_COLOR[signalHazardCategory(signal) ?? ""] ?? CATEGORY_COLOR_FALLBACK, fontWeight: 800 }}>{signal.source}</span>
                  {" · "}{signal.geography?.location}{" · "}{signal.title}
                </div>
              ))}
            </section>
          )}

          {/* Footer */}
          {data && (
            <div style={{ marginTop: 16, borderTop: "1px solid #1a1a1a", paddingTop: 10 }}>
              <p style={{ color: "#444", fontSize: 10, margin: 0 }}>
                Generated {new Date(data.generatedAt).toLocaleTimeString()} · {geoSignals.length} signals mapped · tiles: CartoDB Dark Matter (OSM)
              </p>
              <p style={{ color: "#444", fontSize: 10, margin: "4px 0 0" }}>
                WHO country-scope boundaries: <a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener noreferrer" style={{ color: "#777" }}>Natural Earth 1:110m</a> (public domain). Boundaries show country scope, not incident coordinates.
              </p>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Maps;
